import alt from 'components/Dispatcher';
import AuthActions from 'actions/AuthActions';
import User from 'records/User';
import {browserHistory} from 'react-router';
import LocalStorageJson from 'utils/localStorageJson';
import {Store} from '@store/@types/alt';
import {Int} from '@store/@types/util';
import {deptCookieName} from 'stores/DeptCookieName';
import {LocalStorageKey} from '@store/@types/util/LocalStorageKey';
import {getDashboardPath} from 'components/StatsPage2.utils';
import {
  clearLocalStorage,
  deleteAllCookies,
  deleteCookie,
  LoginErrorType,
} from 'stores/AuthStore.utils';
import {LogoutParams} from 'stores/AuthSource';
import hasFlag from 'utils/hasFlag';
import {featureFlags} from 'utils/const';

interface AuthStoreState {
  // Technically this should be nullable, but we use it everywhere
  // and I'm not trying to add that many null checks when it's almost always defined
  user: User;
  loginError: LoginErrorType | null;
  loadingAuth: boolean;
  nextPathname: string | null;
  // we increment this on every failed login so reCaptcha knows to reset.
  failCount: Int;
}

class AuthStore extends Store<AuthStoreState> {
  constructor() {
    super();
    const userJson = LocalStorageJson.get(LocalStorageKey.AUTH_USER, null);
    const userObject = userJson ? new User(userJson) : (null as any);
    this.state = {
      loginError: null,
      nextPathname: null,
      user: userObject,
      loadingAuth: false,
      failCount: 0,
    };
    this.bindActions(AuthActions);
  }

  onLogin(data) {
    this.setState({loadingAuth: true});
  }
  onLoginSuccess(user: User) {
    // Reset department filter cookie on login
    deleteCookie(deptCookieName);

    // Save User info to local storage for use when reusing a session later
    localStorage.setItem(LocalStorageKey.AUTH_USER, JSON.stringify(user.toJS()));

    this.setState({
      user,
      loginError: null,
      failCount: 0,
      loadingAuth: false,
    });

    // Timeout here to avoid alt dispatch errors
    setTimeout(() => {
      if (this.state.nextPathname) {
        browserHistory.push(this.state.nextPathname);
        this.setState({nextPathname: null});
      } else {
        if (hasFlag(featureFlags.newCoachHomepage, user)) {
          browserHistory.push('/home');
        } else {
          browserHistory.push(getDashboardPath(user));
        }
      }
    }, 0);
  }
  onLoginError(error) {
    this.setState({
      loginError: error?.response?.data?.explanation || LoginErrorType.INVALID_CREDENTIALS,
      user: null as any,
      loadingAuth: false,
      failCount: this.state.failCount + 1,
    });
  }

  onLogout({nextPathname, message}: LogoutParams) {
    browserHistory.push({pathname: nextPathname ?? '/login', state: {message}});
  }
  onLogoutError(error) {
    console.error(error);
    clearLocalStorage();
    deleteAllCookies();
    alt.recycle();
  }
  onLogoutSuccess() {
    clearLocalStorage();
    deleteAllCookies();
    alt.recycle();
  }

  onSaveNext(pathname) {
    this.setState({nextPathname: pathname});
  }

  /**
   * This seems like the kind of thing that could introduce some weird errors.
   * The payload here is slightly different than the normal auth payload.
   * Can we move this endpoint to kotlin or better yet, remove it entirely?
   */
  onGetUserMeInfoSuccess(response) {
    const user = response.data.me;
    const time = new Date().getTime();
    user.avatar = `${user.avatar}?${time}`;
    user.accountName = user['account:name'];
    user.accountId = user['account:id'];
    localStorage.setItem(LocalStorageKey.AUTH_USER, JSON.stringify(user));
    this.setState({user: new User(user)});
  }
}

export default alt.createStore(AuthStore);
